import { MatchTransportError } from "./errors";
import type {
  AttackMessage,
  CheckpointMessage,
  ControlEnvelope,
  MatchEnvelope,
  PiecePoseMessage,
  PoseEnvelope,
} from "./protocol";
import {
  POSE_INTERVAL_TICKS,
  attackIdFor,
  validateAttackIdentity,
} from "./protocol";

export class SequenceAllocator {
  private nextValue: number;

  constructor(firstSequence = 1) {
    if (!Number.isSafeInteger(firstSequence) || firstSequence < 1) {
      throw new RangeError("firstSequence must be a positive safe integer");
    }
    this.nextValue = firstSequence;
  }

  next(): number {
    if (!Number.isSafeInteger(this.nextValue)) {
      throw new MatchTransportError("protocol", "Sequence number space exhausted");
    }
    const result = this.nextValue;
    this.nextValue += 1;
    return result;
  }

  get nextSequence(): number {
    return this.nextValue;
  }

  reset(firstSequence = 1): void {
    if (!Number.isSafeInteger(firstSequence) || firstSequence < 1) {
      throw new RangeError("firstSequence must be a positive safe integer");
    }
    this.nextValue = firstSequence;
  }
}

export interface SequenceObservation {
  readonly accepted: boolean;
  readonly duplicate: boolean;
  readonly gap: boolean;
  readonly previous: number;
  readonly highest: number;
}

/** Reliable ordered high-water tracker with explicit reconnect-gap reporting. */
export class ReliableSequenceTracker {
  private highWater = 0;

  observe(sequence: number): SequenceObservation {
    if (!Number.isSafeInteger(sequence) || sequence < 1) {
      throw new MatchTransportError("protocol", "Invalid reliable sequence number");
    }
    const previous = this.highWater;
    if (sequence <= previous) {
      return { accepted: false, duplicate: true, gap: false, previous, highest: previous };
    }
    const gap = sequence !== previous + 1;
    this.highWater = sequence;
    return { accepted: true, duplicate: false, gap, previous, highest: sequence };
  }

  get highestReceived(): number {
    return this.highWater;
  }

  restore(highestReceived: number): void {
    if (!Number.isSafeInteger(highestReceived) || highestReceived < 0) {
      throw new RangeError("highestReceived must be a non-negative safe integer");
    }
    this.highWater = highestReceived;
  }
}

export interface JournalEntry {
  readonly envelope: ControlEnvelope;
  readonly sentAtMs: number;
  acknowledgedAtMs?: number;
}

export interface CheckpointRecord {
  readonly senderId: string;
  readonly message: CheckpointMessage;
  readonly envelopeSeq: number;
  readonly recordedAtMs: number;
  hashMatched: boolean;
}

/**
 * Retains control events for reconnect replay. Acknowledged records remain for
 * the configured retention window (30 s by default), instead of being deleted
 * immediately, so a transcript can be replayed from the last checkpoint.
 */
export class ReconnectJournal {
  readonly retentionMs: number;
  private readonly outgoing = new Map<number, JournalEntry>();
  private readonly checkpoints: CheckpointRecord[] = [];

  constructor(retentionMs = 30_000) {
    if (!Number.isFinite(retentionMs) || retentionMs < 30_000) {
      throw new RangeError("Reconnect journal retention must be at least 30 seconds");
    }
    this.retentionMs = retentionMs;
  }

  recordOutgoing(envelope: ControlEnvelope, sentAtMs: number): void {
    const existing = this.outgoing.get(envelope.seq);
    if (existing && existing.envelope !== envelope) {
      throw new MatchTransportError(
        "protocol",
        `Reliable sequence ${envelope.seq} was reused`,
      );
    }
    if (!existing) this.outgoing.set(envelope.seq, { envelope, sentAtMs });
    if (envelope.message.type === "checkpoint") {
      this.recordCheckpoint(envelope as MatchEnvelope<CheckpointMessage>, sentAtMs);
    }
  }

  recordIncomingCheckpoint(
    envelope: MatchEnvelope<CheckpointMessage>,
    receivedAtMs: number,
  ): void {
    this.recordCheckpoint(envelope, receivedAtMs);
  }

  private recordCheckpoint(
    envelope: MatchEnvelope<CheckpointMessage>,
    recordedAtMs: number,
  ): void {
    if (
      this.checkpoints.some(
        (checkpoint) =>
          checkpoint.senderId === envelope.senderId &&
          checkpoint.envelopeSeq === envelope.seq,
      )
    ) {
      return;
    }
    this.checkpoints.push({
      senderId: envelope.senderId,
      message: envelope.message,
      envelopeSeq: envelope.seq,
      recordedAtMs,
      hashMatched: false,
    });
  }

  acknowledgeThrough(sequence: number, acknowledgedAtMs: number): number {
    let changed = 0;
    for (const [entrySequence, entry] of this.outgoing) {
      if (entrySequence <= sequence && entry.acknowledgedAtMs === undefined) {
        entry.acknowledgedAtMs = acknowledgedAtMs;
        changed += 1;
      }
    }
    return changed;
  }

  markCheckpointHashMatched(senderId: string, stateHash: string): boolean {
    for (let index = this.checkpoints.length - 1; index >= 0; index -= 1) {
      const checkpoint = this.checkpoints[index];
      if (checkpoint.senderId === senderId && checkpoint.message.stateHash === stateHash) {
        checkpoint.hashMatched = true;
        return true;
      }
    }
    return false;
  }

  latestMatchedCheckpoint(): CheckpointRecord | undefined {
    return [...this.checkpoints]
      .filter((checkpoint) => checkpoint.hashMatched)
      .sort(
        (left, right) =>
          right.message.checkpointTick - left.message.checkpointTick ||
          right.recordedAtMs - left.recordedAtMs,
      )[0];
  }

  /** Events the remote says it has not seen, in original sequence order. */
  replayAfter(sequence: number): readonly ControlEnvelope[] {
    return [...this.outgoing.values()]
      .filter((entry) => entry.envelope.seq > sequence)
      .sort((left, right) => left.envelope.seq - right.envelope.seq)
      .map((entry) => entry.envelope);
  }

  unacknowledged(): readonly ControlEnvelope[] {
    return [...this.outgoing.values()]
      .filter((entry) => entry.acknowledgedAtMs === undefined)
      .sort((left, right) => left.envelope.seq - right.envelope.seq)
      .map((entry) => entry.envelope);
  }

  prune(nowMs: number): void {
    const cutoff = nowMs - this.retentionMs;
    for (const [sequence, entry] of this.outgoing) {
      if (
        entry.acknowledgedAtMs !== undefined &&
        entry.acknowledgedAtMs < cutoff &&
        entry.sentAtMs < cutoff
      ) {
        this.outgoing.delete(sequence);
      }
    }
    while (
      this.checkpoints.length > 0 &&
      this.checkpoints[0].recordedAtMs < cutoff &&
      !this.checkpoints[0].hashMatched
    ) {
      this.checkpoints.shift();
    }
  }

  clear(): void {
    this.outgoing.clear();
    this.checkpoints.length = 0;
  }
}

interface PoseHighWater {
  readonly poseTick: number;
  readonly sequence: number;
}

/** Drops stale/reordered pose packets independently for each sender. */
export class NewestPoseFilter {
  private readonly newest = new Map<string, PoseHighWater>();

  accept(envelope: PoseEnvelope & { readonly message: PiecePoseMessage }): boolean {
    const previous = this.newest.get(envelope.senderId);
    const candidate: PoseHighWater = {
      poseTick: envelope.message.poseTick,
      sequence: envelope.seq,
    };
    if (
      previous &&
      (candidate.poseTick < previous.poseTick ||
        (candidate.poseTick === previous.poseTick && candidate.sequence <= previous.sequence))
    ) {
      return false;
    }
    this.newest.set(envelope.senderId, candidate);
    return true;
  }

  clear(senderId?: string): void {
    if (senderId === undefined) this.newest.clear();
    else this.newest.delete(senderId);
  }
}

/** Supports the original 150 ms cadence: 18 ticks at 120 Hz. */
export class PoseCadence {
  readonly intervalTicks: number;
  private lastSentTick: number | undefined;

  constructor(intervalTicks = POSE_INTERVAL_TICKS) {
    if (!Number.isSafeInteger(intervalTicks) || intervalTicks < 1) {
      throw new RangeError("Pose interval must be a positive integer tick count");
    }
    this.intervalTicks = intervalTicks;
  }

  shouldSend(tick: number, force = false): boolean {
    if (!Number.isSafeInteger(tick) || tick < 0) return false;
    if (
      !force &&
      this.lastSentTick !== undefined &&
      tick - this.lastSentTick < this.intervalTicks
    ) {
      return false;
    }
    this.lastSentTick = tick;
    return true;
  }

  reset(): void {
    this.lastSentTick = undefined;
  }
}

/** Bounded senderId/sourceLockSeq dedupe, independent of untrusted attackId. */
export class AttackDeduplicator {
  readonly maximumEntries: number;
  private readonly seen = new Set<string>();
  private readonly order: string[] = [];

  constructor(maximumEntries = 4_096) {
    if (!Number.isSafeInteger(maximumEntries) || maximumEntries < 64) {
      throw new RangeError("maximumEntries must be an integer of at least 64");
    }
    this.maximumEntries = maximumEntries;
  }

  accept(senderId: string, sourceLockSeq: number): boolean {
    const key = attackIdFor(senderId, sourceLockSeq);
    if (this.seen.has(key)) return false;
    this.seen.add(key);
    this.order.push(key);
    if (this.order.length > this.maximumEntries) {
      const removed = this.order.shift();
      if (removed !== undefined) this.seen.delete(removed);
    }
    return true;
  }

  clear(): void {
    this.seen.clear();
    this.order.length = 0;
  }
}

export interface ScheduledAttack {
  readonly senderId: string;
  readonly message: AttackMessage;
  /** True when it arrived after an already-processed eligible boundary. */
  readonly late: boolean;
}

/**
 * Queues by shared eligibleTick and only releases at a local lock boundary.
 * Packet arrival time never changes eligibleTick; late arrivals naturally move
 * to the next boundary and are surfaced as `late`.
 */
export class ScheduledAttackInbox {
  private readonly dedupe: AttackDeduplicator;
  private readonly pending: ScheduledAttack[] = [];
  private lastLockBoundaryTick = -1;

  constructor(maximumDedupeEntries = 4_096) {
    this.dedupe = new AttackDeduplicator(maximumDedupeEntries);
  }

  receive(envelope: MatchEnvelope<AttackMessage>): ScheduledAttack | undefined {
    if (!validateAttackIdentity(envelope)) {
      throw new MatchTransportError("protocol", "Attack identity does not match its source lock");
    }
    if (!this.dedupe.accept(envelope.senderId, envelope.message.sourceLockSeq)) {
      return undefined;
    }
    const scheduled: ScheduledAttack = {
      senderId: envelope.senderId,
      message: envelope.message,
      late: envelope.message.eligibleTick <= this.lastLockBoundaryTick,
    };
    this.pending.push(scheduled);
    this.pending.sort(
      (left, right) =>
        left.message.eligibleTick - right.message.eligibleTick ||
        left.message.createdTick - right.message.createdTick ||
        left.senderId.localeCompare(right.senderId) ||
        left.message.sourceLockSeq - right.message.sourceLockSeq,
    );
    return scheduled;
  }

  takeForLockBoundary(lockTick: number): readonly ScheduledAttack[] {
    if (!Number.isSafeInteger(lockTick) || lockTick < this.lastLockBoundaryTick) {
      throw new MatchTransportError("protocol", "Lock boundary ticks must be monotonic");
    }
    this.lastLockBoundaryTick = lockTick;
    const eligible: ScheduledAttack[] = [];
    while (
      this.pending.length > 0 &&
      this.pending[0].message.eligibleTick <= lockTick
    ) {
      const attack = this.pending.shift();
      if (attack !== undefined) eligible.push(attack);
    }
    return eligible;
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  clear(): void {
    this.pending.length = 0;
    this.dedupe.clear();
    this.lastLockBoundaryTick = -1;
  }
}
